import TouzenLegacyApp from '../../domains/touzen/entry/TouzenLegacyApp';
import YowanApp from '../../domains/yowan/entry/YowanApp';

const YOWAN_HOSTS = new Set([
  'yowan.jp',
  'www.yowan.jp',
  'yowan-stg.shimashou.com',
  'yowan-prod.shimashou.com',
]);
const TOUZEN_HOSTS = new Set([
  'touzen.jp',
  'www.touzen.jp',
  'touzen-stg.shimashou.com',
  'touzen-official-front-v5.herokuapp.com',
  'test-touzen-official-front-v5.herokuapp.com',
]);
const LOCALHOST_HOSTS = new Set(['localhost', '127.0.0.1', '::1']);
const DEVELOPMENT_ENVIRONMENT = 'development';
const DEVELOPMENT_YOWAN_DIRECT_PATH_PREFIXES = [
  '/admin/content/page-contents',
  '/onboarding',
  '/password-setup/confirm',
  '/yowan/notifications/unsubscribe',
];
const DOMAIN_APPS = Object.freeze({
  touzen: 'touzen',
  yowan: 'yowan',
});
const DOMAIN_HEAD_ASSETS = Object.freeze({
  touzen: {
    icon: { href: '/touzen/favicon-48.png', type: 'image/png', sizes: '48x48' },
    appleTouchIcon: { href: '/touzen/apple-touch-icon.png', sizes: '180x180' },
    manifest: { href: '/touzen/manifest.json' },
  },
  yowan: {
    icon: { href: '/yowan/favicon-32.png', type: 'image/png', sizes: '32x32' },
    appleTouchIcon: { href: '/yowan/apple-touch-icon.png', sizes: '180x180' },
    manifest: { href: '/yowan/manifest.json' },
  },
});

const setLinkElement = (selector, rel, attributes = {}) => {
  if (typeof document === 'undefined') {
    return;
  }

  let element = document.querySelector(selector);

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (value) {
      element.setAttribute(key, value);
      return;
    }

    element.removeAttribute(key);
  });
};

const syncDomainHeadAssets = (domainApp) => {
  if (typeof document === 'undefined') {
    return;
  }

  const assets = DOMAIN_HEAD_ASSETS[domainApp] || DOMAIN_HEAD_ASSETS.touzen;

  setLinkElement('link[rel="icon"]', 'icon', {
    href: assets.icon.href,
    type: assets.icon.type,
    sizes: assets.icon.sizes,
  });
  setLinkElement('link[rel="apple-touch-icon"]', 'apple-touch-icon', {
    href: assets.appleTouchIcon.href,
    sizes: assets.appleTouchIcon.sizes,
  });
  setLinkElement('link[rel="manifest"]', 'manifest', {
    href: assets.manifest.href,
  });
};

// Development-only helper:
// local verification can start the Yowan app explicitly with ?domain=yowan,
// while staging / production remain purely host-routed.
const resolveDevelopmentYowanPreviewOverride = (hostname) => {
  if (
    process.env.REACT_APP_ENVIRONMENT !== DEVELOPMENT_ENVIRONMENT ||
    !LOCALHOST_HOSTS.has(hostname) ||
    typeof window === 'undefined'
  ) {
    return null;
  }

  const override = new URLSearchParams(window.location.search).get('domain');
  if (override === 'yowan') {
    return override;
  }

  const pathname = window.location.pathname || '';
  if (DEVELOPMENT_YOWAN_DIRECT_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return 'yowan';
  }

  return null;
};

const resolveRuntimeInjectedDomainApp = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.__NODE_V11_DOMAIN_APP__ === DOMAIN_APPS.yowan ? DOMAIN_APPS.yowan : null;
};

export const resolveDomainApp = (hostname = '') => {
  const normalized = hostname.toLowerCase();
  const yowanPreviewOverride = resolveDevelopmentYowanPreviewOverride(normalized);
  const runtimeInjectedDomainApp = resolveRuntimeInjectedDomainApp();
  const isYowan =
    YOWAN_HOSTS.has(normalized) || normalized.endsWith('.yowan.jp');
  const isTouzen =
    TOUZEN_HOSTS.has(normalized) || normalized.endsWith('.touzen.jp');

  if (yowanPreviewOverride === 'yowan') {
    syncDomainHeadAssets(DOMAIN_APPS.yowan);
    return YowanApp;
  }

  if (isYowan) {
    syncDomainHeadAssets(DOMAIN_APPS.yowan);
    return YowanApp;
  }

  if (isTouzen) {
    syncDomainHeadAssets(DOMAIN_APPS.touzen);
    return TouzenLegacyApp;
  }

  if (runtimeInjectedDomainApp === DOMAIN_APPS.yowan) {
    syncDomainHeadAssets(DOMAIN_APPS.yowan);
    return YowanApp;
  }

  syncDomainHeadAssets(isYowan ? DOMAIN_APPS.yowan : DOMAIN_APPS.touzen);

  return isYowan ? YowanApp : TouzenLegacyApp;
};
